import { useMemo, useState } from 'react';
import { normaliseHeader, parseTable, readXlsx } from '../reports/engine';
import { userService } from '../services/userService';
import './BulkUserImport.css';

const BATCH_SIZE = 200;

/** Reads .xlsx / .csv / .txt into rows of cell text (first sheet with rows). */
async function readRows(file) {
  if (/\.xls$/i.test(file.name)) {
    throw new Error('Old .xls files are not supported. Save the file as .xlsx or CSV and try again.');
  }
  if (/\.xlsx$/i.test(file.name)) {
    const sheets = await readXlsx(new Uint8Array(await file.arrayBuffer()));
    const sheet = sheets.find((item) => item.rows.length > 0);
    return sheet ? sheet.rows.map((row) => row.cells.map((cell) => String(cell ?? ''))) : [];
  }
  return rowsFromText(await file.text());
}

function rowsFromText(text) {
  if (!String(text || '').trim()) return [];
  const { rows } = parseTable(String(text).replace(/^﻿/, ''));
  return rows.map((row) => row.cells.map((cell) => String(cell ?? '')));
}

/** Matches header cells to column keys by label or alias. */
function mapHeader(headerRow, columns) {
  const normalised = headerRow.map((cell) => normaliseHeader(cell));
  const mapping = {};
  columns.forEach((column) => {
    const names = [column.key, column.label, ...(column.aliases || [])].map(normaliseHeader);
    mapping[column.key] = normalised.findIndex((cell) => names.includes(cell));
  });
  return mapping;
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvCell(value) {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Bulk add users from an Excel / CSV file or pasted rows.
 *
 * columns: [{ key, label, required, aliases, hint }]
 * toPayload(record) -> { payload } | { error }  (record = { [key]: text })
 * sampleRows: example rows for the template file.
 */
function BulkUserImport({ title, intro, columns, sampleRows, templateName, toPayload, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [source, setSource] = useState('');
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  const parsed = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const mapping = mapHeader(rows[0], columns);
    const missing = columns.filter((column) => column.required && mapping[column.key] < 0);
    if (missing.length) {
      return { missing, items: [] };
    }
    const items = [];
    rows.slice(1).forEach((cells, offset) => {
      if (!cells.some((cell) => cell.trim() !== '')) return;
      const record = {};
      columns.forEach((column) => {
        const index = mapping[column.key];
        record[column.key] = index >= 0 ? String(cells[index] ?? '').trim() : '';
      });
      const result = toPayload(record);
      items.push({ line: offset + 2, record, ...result });
    });
    return { missing: [], items };
  }, [rows, columns, toPayload]);

  const ready = parsed ? parsed.items.filter((item) => item.payload) : [];
  const invalid = parsed ? parsed.items.filter((item) => item.error) : [];

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setResults(null);
    try {
      const read = await readRows(file);
      if (read.length < 2) throw new Error('The file needs a header row and at least one user.');
      setRows(read);
      setSource(file.name);
    } catch (err) {
      setError(err.message);
    }
  };

  const usePasted = () => {
    setError('');
    setResults(null);
    const read = rowsFromText(pasted);
    if (read.length < 2) {
      setError('Paste a header row and at least one user row.');
      return;
    }
    setRows(read);
    setSource('pasted rows');
  };

  const downloadTemplate = () => {
    const lines = [columns.map((column) => csvCell(column.label)).join(',')];
    (sampleRows || []).forEach((row) => {
      lines.push(columns.map((column) => csvCell(row[column.key])).join(','));
    });
    downloadText(templateName || 'users-template.csv', `${lines.join('\n')}\n`);
  };

  const submit = async () => {
    if (!ready.length) return;
    setError('');
    const all = [];
    try {
      for (let start = 0; start < ready.length; start += BATCH_SIZE) {
        const batch = ready.slice(start, start + BATCH_SIZE);
        setProgress({ done: start, total: ready.length });
        // eslint-disable-next-line no-await-in-loop
        const response = await userService.bulkCreate(batch.map((item) => item.payload));
        (response.results || []).forEach((result, index) => {
          all.push({ ...result, line: batch[index].line });
        });
      }
      setProgress(null);
      setResults(all);
      if (all.some((item) => item.ok)) onDone?.();
    } catch (err) {
      setProgress(null);
      if (all.length) setResults(all);
      setError(`Upload stopped: ${err.message}`);
    }
  };

  const failedResults = results ? results.filter((item) => !item.ok) : [];
  const createdCount = results ? results.filter((item) => item.ok).length : 0;

  return (
    <div className="bulk-modal">
      <div className="bulk-modal__backdrop" onClick={progress ? undefined : onClose} />
      <div className="bulk-modal__panel" role="dialog" aria-modal="true" aria-label={title}>
        <div className="bulk-modal__header">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} disabled={Boolean(progress)} aria-label="Close">
            ×
          </button>
        </div>

        <div className="bulk-modal__body">
          {intro ? <p className="bulk-muted">{intro}</p> : null}

          <div className="bulk-columns">
            <strong>Columns</strong>
            <ul>
              {columns.map((column) => (
                <li key={column.key}>
                  <code>{column.label}</code>
                  {column.required ? <em> required</em> : null}
                  {column.hint ? <span> — {column.hint}</span> : null}
                </li>
              ))}
            </ul>
            <button type="button" className="bulk-link" onClick={downloadTemplate}>
              Download template (CSV)
            </button>
          </div>

          {!results ? (
            <>
              <div className="bulk-source">
                <label className="bulk-file">
                  {source && source !== 'pasted rows' ? `File: ${source}` : 'Choose Excel or CSV file…'}
                  <input
                    type="file"
                    accept=".xlsx,.csv,.txt,.tsv"
                    onChange={chooseFile}
                    disabled={Boolean(progress)}
                  />
                </label>
                <span className="bulk-muted">or paste rows (with the header row) copied from Excel:</span>
                <textarea
                  rows={4}
                  value={pasted}
                  onChange={(event) => setPasted(event.target.value)}
                  placeholder={columns.map((column) => column.label).join('\t')}
                  disabled={Boolean(progress)}
                  aria-label="Pasted rows"
                />
                <button
                  type="button"
                  className="bulk-button bulk-button--ghost"
                  onClick={usePasted}
                  disabled={!pasted.trim() || Boolean(progress)}
                >
                  Check pasted rows
                </button>
              </div>

              {error ? <p className="bulk-error">{error}</p> : null}

              {parsed && parsed.missing.length ? (
                <p className="bulk-error">
                  Missing column{parsed.missing.length > 1 ? 's' : ''}:{' '}
                  {parsed.missing.map((column) => column.label).join(', ')}. Check the header row
                  matches the template.
                </p>
              ) : null}

              {parsed && !parsed.missing.length ? (
                <>
                  <p className="bulk-summary">
                    <strong>{ready.length}</strong> ready to add
                    {invalid.length ? (
                      <>
                        {' · '}
                        <strong className="bulk-bad">{invalid.length}</strong> with problems (not added)
                      </>
                    ) : null}
                    {' '}from {source}.
                  </p>
                  <div className="bulk-table-scroll">
                    <table className="bulk-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          {columns.map((column) => (
                            <th key={column.key}>{column.label}</th>
                          ))}
                          <th>Check</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.items.slice(0, 200).map((item) => (
                          <tr key={item.line} className={item.error ? 'bulk-row--bad' : ''}>
                            <td>{item.line}</td>
                            {columns.map((column) => (
                              <td key={column.key}>
                                {column.secret && item.record[column.key]
                                  ? '••••'
                                  : item.record[column.key] || '—'}
                              </td>
                            ))}
                            <td>{item.error || 'OK'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {parsed.items.length > 200 ? (
                    <p className="bulk-muted">Showing the first 200 rows.</p>
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <>
              <p className="bulk-summary">
                <strong>{createdCount}</strong> user{createdCount === 1 ? '' : 's'} added
                {failedResults.length ? (
                  <>
                    {' · '}
                    <strong className="bulk-bad">{failedResults.length}</strong> not added
                  </>
                ) : null}
                {invalid.length ? ` · ${invalid.length} skipped before upload` : ''}.
              </p>
              {error ? <p className="bulk-error">{error}</p> : null}
              {failedResults.length || invalid.length ? (
                <div className="bulk-table-scroll">
                  <table className="bulk-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Email</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...invalid.map((item) => ({ line: item.line, email: item.record.email, error: item.error })), ...failedResults]
                        .sort((a, b) => a.line - b.line)
                        .map((item) => (
                          <tr key={`${item.line}-${item.email}`} className="bulk-row--bad">
                            <td>{item.line}</td>
                            <td>{item.email || '—'}</td>
                            <td>{item.error}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="bulk-modal__footer">
          {results ? (
            <button type="button" className="bulk-button" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="bulk-button bulk-button--ghost"
                onClick={onClose}
                disabled={Boolean(progress)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bulk-button"
                onClick={submit}
                disabled={!ready.length || Boolean(progress)}
              >
                {progress
                  ? `Adding ${Math.min(progress.done + BATCH_SIZE, progress.total)} of ${progress.total}…`
                  : `Add ${ready.length} user${ready.length === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkUserImport;
